import { Component, EventEmitter, Input, OnChanges, Output, ViewChild, ViewEncapsulation, inject, ChangeDetectionStrategy } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Observable } from "rxjs";
import { first } from "rxjs/operators";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { LorenUserInfo } from "../../../../../../shared/interfaces/loren-user-info.interface";
import { LorenFileUploadComponent } from "../../../../../../shared/modules/loren-file-upload/components/loren-file-upload/loren-file-upload.component";
import { LorenImageCropperDialogComponent } from "../../../../../../shared/modules/loren-image-cropper-dialog/components/loren-image-cropper-dialog/loren-image-cropper-dialog.component";
import { LorenImageCropperData } from "../../../../../../shared/modules/loren-image-cropper-dialog/interfaces/loren-image-cropper-data.interface";
import { LorenUserServiceService } from "../../../../../loren-user-settings/services/loren-user-service/loren-user-service.service";
import { LorenFileUploadModule } from "../../../../../../shared/modules/loren-file-upload/loren-file-upload.module";
import { MatMenuModule } from "@angular/material/menu";
import { MatIconModule } from "@angular/material/icon";
import { MatDividerModule } from "@angular/material/divider";
import { MatIconButton } from "@angular/material/button";

@Component({
  imports: [
    MatIconButton,
    MatMenuModule,
    MatIconModule,
    MatDividerModule,
    TranslocoPipe,
    LorenFileUploadModule,
  ],
  encapsulation: ViewEncapsulation.None,
  selector: "loren-user-profile-picture",
  standalone: true,
  styleUrls: ["./loren-user-profile-picture.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: "./loren-user-profile-picture.component.html",
})
export class LorenUserProfilePictureComponent implements OnChanges {
  dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private translateService = inject(TranslocoService);
  private userService = inject(LorenUserServiceService);


  @Input() userInfo: LorenUserInfo;
  @Output() userInfoUpdated = new EventEmitter<LorenUserInfo>();
  userPic: string;
  fileSizeLimitInMb = 4;
  allowedFileTypes = ".png, .jpg, .jpeg";

  @ViewChild(LorenFileUploadComponent)
  fileUploadComponent: LorenFileUploadComponent;

  ngOnChanges(): void {
    this.userPic = this.userInfo.image;
  }

  showFileSizeExceededError() {
    this.translateService
      .selectTranslate("errors.file_size_exceeded", {
        value: this.fileSizeLimitInMb,
      })
      .pipe(first())
      .subscribe((translation) => {
        this.snackBar.open(translation, null, {
          duration: 5000,
          panelClass: ["mat-toolbar", "mat-warn"],
        });
      });
  }

  showFileTypeNotAllowedError(fileName: string) {
    this.translateService
      .selectTranslate("errors.file_type_not_allowed", {
        value: fileName,
        values: this.allowedFileTypes,
      })
      .pipe(first())
      .subscribe((translation) => {
        this.snackBar.open(translation, null, {
          duration: 5000,
          panelClass: ["mat-toolbar", "mat-warn"],
        });
      });
  }

  triggerFileSelection() {
    this.fileUploadComponent.triggerFileInputClick();
  }

  handleNewImageUpload(newImage: string) {
    const dialogData: LorenImageCropperData = {
      sourceImage: newImage,
    };

    this.prepareDialog(dialogData).subscribe((result) => {
      if (!result) {
        return;
      }

      this.userInfoUpdated.emit({
        id: this.userInfo.id,
        image: dialogData.croppedImage,
      });
    });
  }

  removePicture() {
    this.userService
      .deleteUserProfilePicture(Number(this.userInfo.id))
      .pipe(first())
      .subscribe(() => {
        this.translateService
          .selectTranslate("user.profile_picture_deleted")
          .pipe(first())
          .subscribe((translation) => {
            this.snackBar.open(translation, null, {
              duration: 5000,
              panelClass: ["mat-toolbar", "mat-primary"],
            });
            this.userPic = "";
            this.userInfoUpdated.emit({
              id: this.userInfo.id,
              image: null,
            });
          });
      });
  }

  private prepareDialog(dialogData: LorenImageCropperData): Observable<unknown> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = true;
    dialogConfig.data = dialogData;
    dialogConfig.maxWidth = "600px";
    dialogConfig.maxHeight = "600px";

    const dialogRef = this.dialog.open(
      LorenImageCropperDialogComponent,
      dialogConfig
    );

    return dialogRef.afterClosed();
  }
}
